/* eslint-disable max-lines-per-function */
import ImageClass from '../../../assets';
import { logoutThunk } from '../../../redux/thunks';
import NotificationPopper from '../../notification/notification_popper';
import { settingSelectedTabForCompany } from '../../../redux/slices/team';
import { useNavigate } from 'react-router';
import { useState } from 'react';
import { AppDispatch, RootState } from '../../../redux/store';
import { Avatar, Box, Divider, Menu, MenuItem, Typography } from '@mui/material';
import { BookOnlineOutlined, CalendarTodayOutlined, Logout, PeopleOutlineOutlined, SettingsOutlined, VerifiedUserOutlined } from '@mui/icons-material';
import { brand, red } from '../../../theme/themePrimitives';
import { useDispatch, useSelector } from 'react-redux';

const UserProfileSettingsComponent = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    // const [notificationAnchorEl, setNotificationAnchorEl] = useState<HTMLElement | null>(null);
    const { currentUser } = useSelector((state: RootState) => state.app);
    const isMenuOpen = Boolean(anchorEl);
    const menuItems = [
        {
            label: 'My Profile',
            icon: <VerifiedUserOutlined fontSize="small" />,
            onClick: () => {
                navigate('/app/profile');
                setAnchorEl(null);
            },
            roles: ['organizationOwner', 'organizationAdmin', 'salesMember', 'salesManager', 'companyManager', 'client', 'user']
        },
        {
            label: 'Calendar',
            icon: <CalendarTodayOutlined fontSize="small" />,
            onClick: () => {
                navigate('/app/calendar');
                setAnchorEl(null);
            },
            roles: ['organizationOwner', 'organizationAdmin', 'user']
        },
        {
            label: 'Integrations',
            icon: <SettingsOutlined fontSize="small" />,
            onClick: () => {
                navigate('/app/integrations');
                setAnchorEl(null);
            },
            roles: ['organizationOwner', 'organizationAdmin', 'salesMember', 'salesManager', 'companyManager', 'client', 'user']
        },
        {
            label: 'Company',
            icon: <PeopleOutlineOutlined fontSize="small" />,
            onClick: () => {
                navigate('/app/company-management');
                setAnchorEl(null);
            },
            roles: ['organizationOwner', 'organizationAdmin', 'salesMember', 'salesManager', 'companyManager', 'client', 'user']
        },
        {
            label: 'Learning Center',
            icon: <BookOnlineOutlined fontSize="small" />,
            onClick: () => {
                window.open('https://gonzy.zendesk.com/hc/en-us/', '_blank');
                setAnchorEl(null);
            },
            roles: ['organizationOwner', 'organizationAdmin', 'salesMember', 'salesManager', 'companyManager', 'client', 'user']
        }
    ];

    /**
         * @heading3: Functions
         * @heading4: Handles Menu Open on hover.
         */
    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget as HTMLElement);
    };

    // const handleNotificationMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    //     setNotificationAnchorEl(event.currentTarget as HTMLElement);
    // };

    /**
     * @heading4: Handles Menu Close on hover out.
     * @code:
     * handleMenuOpen();
     * handleMenuClose();
     */
    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    /**
     * @heading4: Handles logout functionality.
     * @code:
     * handleLogout();
     */
    const handleLogout = async () => {
        // Add your logout logic here
        try {
            await dispatch(logoutThunk()).unwrap();
            navigate('/login');
        } catch (e) {
            // Catch errors
        }
    };

    return (
        <Box display="flex" alignItems="center">
            <Typography variant="h4" fontWeight="bold" paddingRight="10px">
                {currentUser?.agency_name}
            </Typography>
            <NotificationPopper />
            <Box sx={{ ml: 2 }}>
                <Box
                    display="flex"
                    alignItems="center"
                    gap="10px"
                    onClick={handleMenuOpen}
                    aria-controls={isMenuOpen ? 'user-menu' : ''}
                    aria-haspopup="true"
                    style={{
                        cursor: 'pointer'
                    }}
                >
                    <Avatar
                        src={currentUser?.profile_picture ? `${process.env.REACT_APP_FILE_PATH}${currentUser.profile_picture}` : ImageClass.userImage}
                        //   onError={handleError}
                        sx={{
                            width: 40,
                            height: 40,
                            border: `1px solid ${brand[300]}`,
                            cursor: 'pointer',
                            backgroundColor: 'transparent'
                        }}
                        alt="User Profile"
                    />
                    <Box>
                        <Typography variant="subtitle1" fontSize="16px" fontWeight="bold" mb={0} whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis" textAlign="center" sx={{
                            textTransform: 'uppercase'
                        }}>{currentUser?.name || `${currentUser?.first_name || ''} ${currentUser?.last_name || ''}`}</Typography>
                        <Typography variant="body2" fontSize="11px" color="textSecondary" mt={0} whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis" textAlign="center">
                            {currentUser?.role === 'organizationOwner'
                                ? 'ORGANIZATION OWNER'
                                : currentUser?.role === 'organizationAdmin'
                                    ? 'ORGANIZATION ADMIN'
                                    : currentUser?.role === 'salesMember'
                                        ? 'SALES MEMBER'
                                        : currentUser?.role === 'salesManager'
                                            ? 'SALES MANAGER'
                                            : currentUser?.role === 'companyManager'
                                                ? 'COMPANY MANAGER'
                                                : currentUser?.role === 'client'
                                                    ? 'CUSTOMER'
                                                    : 'USER'}
                        </Typography>
                        {(currentUser?.subscription_status === 'trialing' || currentUser?.subscription_status === 'past_due') && <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            mt={1}
                        >
                            <Box
                                bgcolor={red[300]}
                                color="white"
                                width="max-content"
                                fontSize="12px"
                                px={2}
                                py="2px"
                                borderRadius={1}
                                style={{
                                    cursor: 'pointer'
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    dispatch(settingSelectedTabForCompany(2));
                                    navigate('/app/company-management');
                                }}
                            >
                                {currentUser?.subscription_status === 'past_due' ? 'Past Due' : 'Trial'}
                            </Box>
                        </Box>}
                    </Box>
                </Box>
                {/* Dropdown Menu */}
                <Menu
                    id="user-menu"
                    anchorEl={anchorEl}
                    open={isMenuOpen}
                    onClose={handleMenuClose}
                    onMouseLeave={handleMenuClose}
                    PaperProps={{
                        style: {
                            transform: 'translateY(10px)',
                            minWidth: '200px'
                        }
                    }}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right'
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right'
                    }}
                >
                    <MenuItem>
                        <Box display="flex" alignItems="center" justifyContent="center" gap="10px">
                            <Avatar
                                src={currentUser?.profile_picture ? `${process.env.REACT_APP_FILE_PATH}${currentUser.profile_picture}` : ImageClass.userImage}
                                //   onError={handleError}
                                sx={{
                                    width: 40,
                                    height: 40,
                                    border: `1px solid ${brand[300]}`,
                                    cursor: 'pointer',
                                    backgroundColor: 'transparent'
                                }}
                                alt="User Profile"
                            />
                            <Box display="flex" flexDirection="column" alignItems="start">
                                <Typography align="center" variant="body1">{currentUser?.email}</Typography>
                                {/* <Typography align="center" variant="body2">Version: <b>{process.env.REACT_APP_BUILD_VERSION}</b></Typography> */}
                                <Typography align="center" variant="body2">Version: <b>1.0.5</b></Typography>
                            </Box>
                        </Box>
                    </MenuItem>
                    <Divider />
                    {menuItems.map((item, index) => {
                        const existInRole = item.roles.find((role) => role === currentUser?.role);
                        if (existInRole) {
                            return <Box key={index}>
                                <MenuItem onClick={() => { handleMenuClose(); item.onClick?.(); }}>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        {item.icon}
                                        <Typography variant="body1">{item.label}</Typography>
                                    </Box>
                                </MenuItem>
                                {index === 2 && <Divider />}
                            </Box>;
                        }
                        return null;
                    })}
                    <Divider />
                    <MenuItem onClick={handleLogout}>
                        <Box display="flex" alignItems="center" gap="10px">
                            <Logout fontSize="small" color="error" />
                            <Typography variant="body1" color="error">
                                Sign Out
                            </Typography>
                        </Box>
                    </MenuItem>
                </Menu>
            </Box>
        </Box>
    );
};

export default UserProfileSettingsComponent;